'use client';

import { useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ArrivalOrderSlotButton } from '@/app/(main)/(pages)/_components/schedules/arrival-order/arrival-order-slot-button.component';
import { SlotsSkeleton } from '@/app/(main)/(pages)/_components/schedules/slots-skeleton.component';
import { formatRelativeDay } from '@/utils/formatters/format-relative-day';
import { useScheduleSlots } from '@/app/(main)/(pages)/_components/schedules/_hooks/use-schedule-slots.hook';
import { SearchCardArrivalOrderModal } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_components/tabs/arrival-order/search-card-arrival-order-modal.component';
import { useSlotFilters } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-slot-filters.hook';
import { Button } from '@/components/ui/button/button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { ScheduleType, SchedulesSearchSlot, SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { dateFormatter } from '@/utils/formatters/date';
import { sumProcedurePrices } from '@/utils/procedures/procedures.util';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

const PREVIEW_LIMIT_DAYS = 1;
const PREVIEW_SLOT_COUNT = 2;

interface ArrivalOrderTabPreviewProps {
  schedule: SearchPartnerData;
  scheduleType?: ScheduleType;
}

export function ArrivalOrderTabPreview({
  schedule,
  scheduleType = ScheduleType.ARRIVAL_ORDER,
}: ArrivalOrderTabPreviewProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const arrivalOrder = schedule.schedulesTypes[scheduleType];
  const scheduleId = arrivalOrder?.[0]?.id;

  const slotFilters = useSlotFilters();

  const { isLoading, days } = useScheduleSlots({
    scheduleId,
    limitDays: PREVIEW_LIMIT_DAYS,
    filters: slotFilters,
  });

  const firstDay = days[0];
  const previewSlots = firstDay?.slots.slice(0, PREVIEW_SLOT_COUNT) ?? [];
  const analytics = getAnalyticsPlugin();

  const handleOpenModal = () => setModalOpen(true);

  const handleSlotClick = (slot: SchedulesSearchSlot) => {
    analytics.track(AnalyticsEnum.CLICK_ARRIVAL_ORDER_SLOT, {
      partner_id: schedule.partner.id,
      data: slot.date,
      faixa_horario: `${dateFormatter(slot.start, 'HH:mm')} às ${dateFormatter(slot.end, 'HH:mm')}`,
      price: sumProcedurePrices(schedule.procedures),
    });
  };

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={DataTestIdEnum.SEARCH_CARD_TAB_ARRIVAL_ORDER}
    >
      {isLoading
        ? (
            <SlotsSkeleton variant="card" />
          )
        : (
            <>
              {firstDay && (
                <Typography variant={TypographyVariant.MEDIUM_BODY_TEXT} className="font-semibold">
                  {`Próximos horários: ${formatRelativeDay(firstDay.date)}`}
                </Typography>
              )}
              {previewSlots.length > 0 && (
                <div className="flex flex-wrap gap-2 w-full">
                  {previewSlots.map((slot) => (
                    <ArrivalOrderSlotButton
                      key={slot.id}
                      start={dateFormatter(slot.start, 'HH:mm')}
                      end={dateFormatter(slot.end, 'HH:mm')}
                      className="flex-1 basis-0 min-w-[120px]"
                      onClick={() => handleSlotClick(slot)}
                    />
                  ))}
                </div>
              )}
              <Button
                variant="default"
                className="w-full h-10 py-0 text-base"
                data-testid={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_OPEN_MODAL}
                onClick={handleOpenModal}
              >
                Ver mais horários
              </Button>
            </>
          )}

      <SearchCardArrivalOrderModal
        scheduleId={scheduleId}
        schedule={schedule}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </div>
  );
}
